import React, { useMemo, useState } from 'react';
import { InlineInputCardWrapper } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { toCssDeclarationName } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/css-declarations';
import { ColorSelectInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/color-select-input';
import {
  parseCssColorToRgba,
  hasImportant,
  stripImportant,
  addImportantIfNeeded,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/parsers';

export interface CssColorInputProps {
  name: string;
  initialValue?: string;
  onChange?: (name: string, value: string) => void;
}

export function CssColorInput({
  name,
  initialValue,
  onChange,
}: CssColorInputProps) {
  const displayName = useMemo(() => toCssDeclarationName(name), [name]);

  // Track if initial value had !important
  const [hadImportant] = useState(() => hasImportant(initialValue || ``));
  const cleanInitialValue = useMemo(
    () => stripImportant(initialValue || ``),
    [initialValue],
  );

  const defaultValue = useMemo(() => {
    if (!cleanInitialValue) return `rgba(0, 0, 0, 0)`;
    if (cleanInitialValue.startsWith(`linear-gradient`)) {
      return cleanInitialValue;
    }
    if (cleanInitialValue.startsWith(`radial-gradient`)) {
      return cleanInitialValue;
    }
    const rgba = parseCssColorToRgba(cleanInitialValue);
    return `rgba(${rgba?.r ?? 0}, ${rgba?.g ?? 0}, ${rgba?.b ?? 0}, ${
      rgba?.a ?? 1
    })`;
  }, [cleanInitialValue]);

  const [color, setColor] = useState(defaultValue);

  const onChangeHandler = (colorStr: string) => {
    if (onChange) {
      setColor(colorStr);
      const outputValue = addImportantIfNeeded(colorStr, hadImportant);
      onChange(name, outputValue);
    }
  };

  return (
    <InlineInputCardWrapper>
      <span>{displayName}</span>
      <ColorSelectInput
        initialValue={color}
        onChange={onChangeHandler}
        showHeader={name === `background`}
      />
    </InlineInputCardWrapper>
  );
}
